<template>
	<div class="chart-board-container" color="panel_main"
		:class="['pa-0', { 'edit-mode-footer': isThreePanelMode, 'flex-column': !isThreePanelMode }]">

		<!-- Edit mode: 3-panel layout -->
		<div v-if="isThreePanelMode" class="three-panel-content">
			<div class="dual-chart-content">
				<div class="chart-panel left-panel">
					<div class="chart-panel-title">{{ $t('Longitudinalsectionview') }}</div>
					<div class="chart-panel-content">
						<ProfileViewDialog ref="profileView" style="width: 100%; height: 100%"></ProfileViewDialog>
					</div>
				</div>
				<div class="chart-panel right-panel">
					<div class="chart-panel-title">
						{{ $t('Crosssectionview') }}
						<span v-if="sectionMarkerLabel" class="section-marker-label">{{ sectionMarkerLabel }}</span>
					</div>
					<div class="chart-panel-content">
						<SectionViewDialog ref="sectionView" style="width: 100%; height: 100%"></SectionViewDialog>
					</div>
				</div>
			</div>
		</div>

		<!-- Normal mode: existing tab layout -->
		<template v-else>
			<div class="tab-wrapper d-flex justify-space-between">
				<div background-color="panel_sub">
					<v-btn variant="flat" v-for="tab in tabs()" :key="tab.id"
						:class="['tab-button', { active: currentTab.id === tab.id }]" @click="onTabClick(tab)">
						{{ $t(tab.title) }}
					</v-btn>
				</div>
				<div class="d-flex justify-end align-center">
					<div v-if="fileName" class="file-name-wrapper d-flex">
						<span class="file-name"> {{ fileName }} </span>
					</div>
				</div>
			</div>
			<component :is="currentTab.component" ref="currentTab" style="width: 100%; height: 100%; overflow: visible">
			</component>
		</template>
	</div>
</template>

<script>
import LineProfileViewDialog from "./LineProfileViewDialog.vue";
import LineSectionViewDialog from "./LineSectionViewDialog.vue";
import MeasurementViewDialog from "./MeasurementViewDialog.vue";
import ProfileViewDialog from "./ProfileViewDialog.vue";
import SectionViewDialog from "./SectionViewDialog.vue";

import * as constants from "@/constant.js";
import { triggerAll } from "@/utils/chartViewUpdaters";

import {
	GoogleAnalyticsClickTypes,
	addClickTypeLog,
} from "../../../utils/googleAnalytics.js";

export default {
	components: {
		ProfileViewDialog,
		SectionViewDialog,
		MeasurementViewDialog,
		LineSectionViewDialog,
		LineProfileViewDialog,
	},
	name: "ChartBoard",
	watch: {
		isEditMode(newVal) {
			// Resize views when switching edit mode
			this.$nextTick(() => {
				if (newVal) {
					triggerAll();
				}
			});
		},
	},
	data: () => ({
		alltabs: Object.values(constants.chartBoardTabs),
		resizeObserver: null,
	}),
	methods: {
		tabs() {
			let availableTabs = this.alltabs;
			availableTabs = this.alltabs.filter(
				(tab) =>
					tab.id !== constants.chartBoardTabs.PROFILE.id &&
					tab.id !== constants.chartBoardTabs.SECTION.id,
			);
			return availableTabs;
		},
		onTabClick(tab) {
			this.currentTab = tab;
			if (tab.id === constants.chartBoardTabs.PROFILE.id) {
				addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_VIEW_LONGITUDINAL);
			} else if (tab.id === constants.chartBoardTabs.SECTION.id) {
				addClickTypeLog(GoogleAnalyticsClickTypes.P_CLICK_VIEW_CROSS_SECTION);
			} else if (tab.id === constants.chartBoardTabs.LINE_PROFILE.id) {
				addClickTypeLog(
					GoogleAnalyticsClickTypes.P_CLICK_VIEW_LINE_LONGITUDINAL,
				);
			} else if (tab.id === constants.chartBoardTabs.LINE_SECTION.id) {
				addClickTypeLog(
					GoogleAnalyticsClickTypes.P_CLICK_VIEW_LINE_CROSS_SECTION,
				);
			} else if (tab.id === constants.chartBoardTabs.MEASUREMENT.id) {
				addClickTypeLog(
					GoogleAnalyticsClickTypes.P_CLICK_VIEW_CUSTOM_CROSS_SECTION,
				);
			}
		},
	},
	created() {
		this.currentTab = constants.chartBoardTabs.LINE_PROFILE;
	},
	mounted() {
		// Use ResizeObserver to monitor ChartBoard container size changes
		const container = this.$el;
		if (container) {
			this.resizeObserver = new ResizeObserver((entries) => {
				entries.forEach(() => {
					triggerAll();
				});
			});
			this.resizeObserver.observe(container);
		}
	},
	beforeUnmount() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
	},
	computed: {
		isEditMode() {
			return this.$store.state.inProgressModeManager.inProgressMode === "EDIT";
		},
		isThreePanelMode() {
			return (
				this.isEditMode &&
				(this.$store.state.inProgressModeManager?.objectType === "road" ||
					this.$store.state.inProgressModeManager?.objectType === "trench")
			);
		},
		currentTab: {
			get() {
				return this.$store.state.chartBoardCurrentTab;
			},
			set(value) {
				this.$store.commit("setChartBoardCurrentTab", value);
			},
		},
		sectionMarkerLabel() {
			// 断面ビューに表示中の断面位置の名称（測点/BC-EC/BP/EP/NO.X+Y）
			return this.$store.state.activeSectionMarkerLabel;
		},
		fileName() {
			if (this.currentTab.id === constants.chartBoardTabs.MEASUREMENT.id) {
				// 任意断面ビュー
				const activeArbitraryCrossSection =
					this.$store.state.measurement.activeArbitraryCrossSection;
				return activeArbitraryCrossSection?.measurementName || "";
			} else if (
				this.currentTab.id === constants.chartBoardTabs.LINE_SECTION.id
			) {
				// 線形断面ビュー
				const activeLineCrossSection = this.$store.state.activeLineCrossSection;
				return activeLineCrossSection?.lineName || "";
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.chart-board-container {
	width: 100%;
	display: flex;
	align-items: start !important;
	transition: all 0.2s;
	background-color: var(--v-panel_main-base);
	overflow: hidden;
}

.chart-board-container.flex-column {
	flex-direction: column;
}

.chart-board-container.edit-mode-footer {
	display: flex !important;
	flex-direction: column !important;
}

.three-panel-content {
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	background-color: #1e1e1e;
}

.dual-chart-content {
	flex: 1;
	display: flex;
	overflow: hidden;
	min-height: 0;
}

.chart-panel {
	flex: 1 1 50%;
	width: 50%;
	display: flex;
	flex-direction: column;
	background-color: #1e1e1e;
	position: relative;
	min-height: 0;
	overflow: hidden;
}

.left-panel {
	border-right: 2px solid #3e3e42;
}

.chart-panel-title {
	padding: 4px 12px;
	background: transparent;
	color: #FFFFFF;
	font-size: 15px;
	height: 36px;
	display: flex;
	align-items: center;
	flex-shrink: 0;
}

.section-marker-label {
	margin-left: 8px;
	color: #FFFFFF;
	font-size: 13px;
}

.chart-panel-content {
	flex: 1;
	min-height: 0;
	overflow: hidden;
	display: flex;
	flex-direction: column;
}

.tab-wrapper {
	width: 100%;
}

.list-ChartContextMenu:hover {
	background-color: #0064ba !important;
}

.tab-button {
	cursor: pointer;
	color: inherit;
	background-color: transparent;
	border-radius: 0;
}

.tab-button:hover {
	background: #302f2f;
}

.tab-button.active {
	background-color: transparent;
	color: #0064ba;
	border-bottom: 0.2em solid #0064ba;
}

.file-name-wrapper {
	min-width: 100px;
}

.file-name {
	display: inline-block;
	border-radius: 4px;
	padding: 4px 8px;
	margin: 1px 1px 1px auto;
	background-color: white;
	color: #21b6fc;
	word-wrap: break-word;
}
</style>
